import { alpha } from '@mui/material/styles'

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`
}

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  750: '#363636',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
}

const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
  contrastText: '#fff'
}
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#4a2bc7',
  dark: '#2a0ba7',
  darker: '#091A7A',
  contrastText: '#fff'
}
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff'
}
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800]
}
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800]
}
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff'
}

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
}

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
  membersChart: '#4a2bc7',
  convoChart: '#2d99ff',
  npsChart: '#2eba88'
}

const BUTTON_COLORS = {
  destroyBackground: '#df685b',
  recordBackground: '#df685b',
  doneBorder: 'solid 1px rgba(145, 158, 171, 0.32)',
  defaultBackground: '#4a2bc7',
  defaultText: '#fff'
}

const TEXT_COLORS = {
  red: '#df685b'
}

const SUBTEXT_COLORS = {
  introVideoSubtext: '#a78d09'
}

const TAGS_COLORS = {
  roomOpenBackground: '#2eba8828',
  roomOpenText: '#228964',
  roomClosedBackground: '#919eab28',
  roomClosedText: '#637381',
  roomUpcomingBackground: '#4a2bc728',
  roomUpcomingText: '#341c94'
}

const LINK_COLORS = {
  textColor: '#4a2bc7'
}

const ROOM_ZERO_STATE_COLORS = {
  backgroundColor: '#ebecf5'
}

export const NPS_RANGE_COLORS = {
  red: '#df685b',
  orange: '#f89853',
  yellow: '#efc90d',
  green: '#2eba88'
}

export const QUESTION_CARD_COLORS = {
  cardBorderColor: '#2eba88',
  buttonbgColor: '#2eba88',
  backgroundColor: '#fff',
  deleteButtonBgColor: '#df685b'
}

export const PROFILE_COVER_COLOR = {
  backgroundColor: '#1b0f4b'
}

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  gradients: GRADIENTS,
  divider: GREY[500_24],
  chart: CHART_COLORS,
  tags: TAGS_COLORS,
  links: LINK_COLORS,
  buttons: BUTTON_COLORS,
  nps: NPS_RANGE_COLORS,
  subtext: SUBTEXT_COLORS,
  texts: TEXT_COLORS,
  roomZeroState: ROOM_ZERO_STATE_COLORS,
  questionCard: QUESTION_CARD_COLORS,
  profileCover: PROFILE_COVER_COLOR,
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
}

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action }
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action }
  }
}

export default palette
